import * as THREE from 'three';
import { emis, metal } from '../materials.ts';
import { G, M, V, eyesGlow, glowSprite, limb, ring } from '../prims.ts';
import { begin, done, type BaseOpts, type Ctx, type Model } from './types.ts';

/*
 * The artificer's clockwork: the automaton (a riveted iron golem with a hammer arm and a piston fist), the
 * colossus (its huge, heavier sibling), the spider-bot (a ticking bomb on six legs) and the gear grenade
 * (a spinning device that drags nightmares in). Dark iron and cold steel with copper-red trim; the life in
 * them is a cyan core. No brass, no gold.
 */

const IRON = metal('#2a2e34', { roughness: 0.55, flatShading: true });
const DARK = metal('#1b1e22', { roughness: 0.65, flatShading: true });
const PLATE = metal('#8e97a2', { roughness: 0.3, flatShading: true });
const PLATE_HI = metal('#b9c1cb', { roughness: 0.25, flatShading: true });
const COPPER = metal('#a4512f', { roughness: 0.4 });
const CORE = emis('#d9fbff', '#3fd4ff', 1.8);
const CORE_SOFT = emis('#9feaff', '#1fb6e6', 1.0);
const RED_EYE = emis('#ffd0c4', '#ff4f3a', 1.8);
const CYAN = '#4fdcff';

/** Canonical frames for the bots (the engine bakes them in this order). */
export type BotPose = 'idle' | 'idleB' | 'walkA' | 'walkB' | 'windup' | 'sweep';
export const BOT_POSES: readonly BotPose[] = ['idle', 'idleB', 'walkA', 'walkB', 'windup', 'sweep'];

/** A clockwork model the game can pose (automaton, colossus). */
export interface BotModel extends Model {
  pose(p: BotPose): void;
}

interface BotRig {
  figure: THREE.Group;
  body: THREE.Group;
  head: THREE.Group;
  armL: THREE.Group;
  armR: THREE.Group;
  legL: THREE.Group;
  legR: THREE.Group;
  weapon: THREE.Group;
}

type Rot = [number, number, number];
interface BotPoseSpec { body?: Rot; head?: Rot; armL?: Rot; armR?: Rot; legL?: Rot; legR?: Rot; weapon?: Rot; figY?: number }

/* Arms and legs hang along -Y: negative X swings them forward, +Z swings the right arm out (−Z the left). */
const BOT_POSE_SPECS: Record<BotPose, BotPoseSpec> = {
  idle: { armR: [-0.12, 0, 0.08], armL: [-0.15, 0, -0.1] },
  idleB: { armR: [-0.08, 0, 0.1], armL: [-0.1, 0, -0.12], body: [0.03, 0, 0], head: [0.06, 0.15, 0], figY: -0.02 },
  walkA: { legL: [-0.38, 0, 0], legR: [0.32, 0, 0], armL: [0.25, 0, -0.1], armR: [-0.3, 0, 0.1], body: [0.08, 0.08, -0.05], figY: 0.03 },
  walkB: { legL: [0.32, 0, 0], legR: [-0.38, 0, 0], armL: [-0.3, 0, -0.1], armR: [0.25, 0, 0.1], body: [0.08, -0.08, 0.05], figY: 0.03 },
  // Hammer cocked high behind the shoulder, the body wound back, feet braced.
  windup: { armR: [-2.5, 0, 0.55], weapon: [0.4, 0, 0], armL: [-0.7, 0, -0.45], body: [-0.1, 0.55, 0], head: [0, -0.3, 0], legL: [-0.3, 0, -0.08], legR: [0.25, 0, 0.08], figY: -0.04 },
  // The sweep: the body uncoils and the hammer arm swings out flat, around it.
  sweep: { armR: [-0.35, 0, 1.45], weapon: [0, 0, 0.3], armL: [0.2, 0, -0.8], body: [0.12, -0.75, 0], head: [0, 0.35, 0], legL: [-0.35, 0, -0.1], legR: [0.3, 0, 0.1], figY: -0.06 },
};

const ZERO: Rot = [0, 0, 0];
const setRot = (g: THREE.Group, r: Rot | undefined): void => {
  const [x, y, z] = r ?? ZERO;
  g.rotation.set(x, y, z);
};

const applyBotPose = (rig: BotRig, p: BotPose): void => {
  const s = BOT_POSE_SPECS[p];
  rig.figure.position.y = s.figY ?? 0;
  setRot(rig.body, s.body);
  setRot(rig.head, s.head);
  setRot(rig.armL, s.armL);
  setRot(rig.armR, s.armR);
  setRot(rig.legL, s.legL);
  setRot(rig.legR, s.legR);
  setRot(rig.weapon, s.weapon);
};

/** A pivot at `at` (in `parent` space) plus an inner frame aligned with the parent's space at rest. */
const pivot = (parent: THREE.Object3D, at: THREE.Vector3): { p: THREE.Group; abs: THREE.Group } => {
  const p = G(parent, at.x, at.y, at.z);
  const abs = G(p, -at.x, -at.y, -at.z);
  return { p, abs };
};

const box = (parent: THREE.Object3D, mat: THREE.Material, w: number, h: number, d: number, x: number, y: number, z: number): THREE.Mesh => {
  const m = M(new THREE.BoxGeometry(w, h, d), mat, parent);
  m.position.set(x, y, z);
  return m;
};

const rivet = (parent: THREE.Object3D, x: number, y: number, z: number, r = 0.022): void => {
  M(new THREE.SphereGeometry(r, 5, 4), PLATE_HI, parent).position.set(x, y, z);
};

/** A flat toothed gear (axis = local Z). */
export const gear = (parent: THREE.Object3D, r: number, teeth: number, mat: THREE.Material, depth = 0.04, hub: THREE.Material = DARK): THREE.Group => {
  const g = G(parent);
  M(new THREE.CylinderGeometry(r * 0.82, r * 0.82, depth, Math.max(8, teeth)), mat, g).rotation.x = Math.PI / 2;
  for (let i = 0; i < teeth; i++) {
    const a = (i / teeth) * Math.PI * 2;
    const t = M(new THREE.BoxGeometry(r * 0.28, r * 0.3, depth), mat, g);
    t.position.set(Math.cos(a) * r * 0.9, Math.sin(a) * r * 0.9, 0);
    t.rotation.z = a;
  }
  M(new THREE.CylinderGeometry(r * 0.25, r * 0.25, depth * 1.6, 8), hub, g).rotation.x = Math.PI / 2;
  return g;
};

interface GolemSpec {
  /** Colossus: broader, heavier, two-handed. */
  big: boolean;
}

/**
 * The clockwork golem. Built at "human and a half" (≈2.5 units); the colossus variant is heavier
 * everywhere (spiked pauldrons, a maul and a piston fist both oversized, three stacks, horned helm).
 */
const clockGolem = (c: Ctx, spec: GolemSpec): { rig: BotRig; core: THREE.Sprite; skel: NonNullable<Model['skel']>; height: number } => {
  const B = spec.big;
  const fig = G(c.g);
  fig.name = 'figure';
  const W = B ? 1.22 : 1; // shoulder width factor
  const HIP = B ? 0.92 : 1.0;

  /* legs: thick iron thighs, steel greaves with pistons, broad feet (a knee pivot under the thigh) */
  const legs: THREE.Group[] = [];
  const knees: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const lx = k * 0.25 * W;
    const { p, abs } = pivot(fig, V(lx, HIP, 0));
    p.name = k < 0 ? 'legL' : 'legR';
    limb(abs, V(lx, HIP, 0), V(lx + k * 0.03, HIP * 0.55, 0.04), (B ? 0.17 : 0.14), (B ? 0.14 : 0.11), IRON, 7);
    // Thigh plate.
    const tp = M(new THREE.CylinderGeometry(B ? 0.19 : 0.155, B ? 0.16 : 0.13, HIP * 0.28, 6, 1, true, -1.2, 2.4), PLATE, abs);
    tp.position.set(lx + k * 0.015, HIP * 0.8, 0.03);
    M(new THREE.SphereGeometry(B ? 0.16 : 0.13, 8, 6), PLATE, abs).position.set(lx + k * 0.03, HIP * 0.55, 0.06);
    const knee = pivot(abs, V(lx + k * 0.03, HIP * 0.55, 0.04));
    knee.p.name = k < 0 ? 'shinL' : 'shinR';
    knees.push(knee.p);
    const kn = knee.abs;
    const shin = M(new THREE.CylinderGeometry(B ? 0.19 : 0.155, B ? 0.15 : 0.12, HIP * 0.42, 6), PLATE, kn);
    shin.position.set(lx + k * 0.03, HIP * 0.31, 0.02);
    // Copper band and a piston behind the calf.
    M(new THREE.CylinderGeometry(B ? 0.195 : 0.16, B ? 0.195 : 0.16, 0.04, 6), COPPER, kn).position.set(lx + k * 0.03, HIP * 0.46, 0.02);
    limb(kn, V(lx + k * 0.05, HIP * 0.52, -0.1), V(lx + k * 0.05, HIP * 0.14, -0.09), 0.022, 0.022, PLATE_HI, 5);
    limb(kn, V(lx + k * 0.05, HIP * 0.52, -0.1), V(lx + k * 0.05, HIP * 0.36, -0.1), 0.04, 0.04, IRON, 6);
    const foot = box(kn, IRON, B ? 0.34 : 0.27, 0.13, B ? 0.5 : 0.42, lx + k * 0.03, 0.065, 0.07);
    foot.rotation.y = k * 0.06;
    const toe = box(kn, PLATE, B ? 0.32 : 0.25, 0.07, 0.14, lx + k * 0.03, 0.12, B ? 0.27 : 0.23);
    toe.rotation.x = 0.35;
    rivet(kn, lx + k * 0.03, 0.15, B ? 0.31 : 0.27);
    legs.push(p);
  }

  /* pelvis (stays with the figure) */
  box(fig, IRON, 0.62 * W, 0.24, 0.38, 0, HIP + 0.04, 0);
  const hipGear = gear(fig, 0.11, 10, COPPER);
  hipGear.position.set(0, HIP + 0.05, 0.2);
  hipGear.name = 'gearHip';

  /* torso */
  const BY = HIP + 0.1;
  const body = pivot(fig, V(0, BY, 0));
  body.p.name = 'body';
  const b = body.abs;
  const TY = BY + 0.48; // torso center
  // A riveted boiler of a chest: a barrel, broad at the ribs, drawn in at the waist and the neck.
  const prof = [[0.3, -0.42], [0.4, -0.3], [0.47, -0.08], [0.5, 0.14], [0.47, 0.32], [0.34, 0.43]] as const;
  const barrel = (k: number): THREE.Vector2[] => prof.map(([r, y]) => new THREE.Vector2(r * W * k, y));
  const torso = M(new THREE.LatheGeometry(barrel(1), 8), IRON, b);
  torso.position.y = TY;
  torso.scale.z = 0.82;
  // Steel breastplate on the front arc, ribbed.
  const chest = M(new THREE.LatheGeometry(barrel(1.04).slice(1, 6), 8, -1.05, 2.1), metal('#8e97a2', { roughness: 0.3, flatShading: true, side: THREE.DoubleSide }), b);
  chest.position.set(0, TY, 0);
  chest.scale.z = 0.84;
  for (const y of [-0.2, 0.32]) {
    const r = y < 0 ? 0.43 : 0.49;
    const rib = M(new THREE.TorusGeometry(r * W * 1.05, 0.018, 3, 10, 2.1), COPPER, b);
    rib.rotation.set(Math.PI / 2, 0, Math.PI / 2 - 1.05);
    rib.position.set(0, TY + y, 0);
    rib.scale.set(1, 0.84, 1);
  }
  // The core: a cyan heart behind a round porthole and a cross grille.
  const CZ = 0.5 * W * 1.04 * 0.84 + 0.01;
  const CY = TY + 0.12;
  const port = M(new THREE.TorusGeometry(B ? 0.17 : 0.13, 0.035, 5, 14), COPPER, b);
  port.position.set(0, CY, CZ);
  M(new THREE.CircleGeometry(B ? 0.15 : 0.115, 14), DARK, b).position.set(0, CY, CZ - 0.02);
  M(new THREE.SphereGeometry(B ? 0.11 : 0.085, 10, 8), CORE, b).position.set(0, CY, CZ - 0.01);
  box(b, DARK, 0.03, (B ? 0.3 : 0.24), 0.02, 0, CY, CZ + 0.02);
  box(b, DARK, (B ? 0.3 : 0.24), 0.03, 0.02, 0, CY, CZ + 0.02);
  const core = glowSprite(b, CYAN, B ? 0.6 : 0.45, V(0, CY, CZ + 0.06), 0.8, true);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    rivet(b, Math.cos(a) * (B ? 0.22 : 0.18), CY + Math.sin(a) * (B ? 0.22 : 0.18), CZ - 0.01);
  }
  // Belly vents glowing faintly.
  for (let i = 0; i < 3; i++) box(b, CORE_SOFT, 0.16 * W, 0.022, 0.02, 0, TY - 0.16 - i * 0.055, 0.4 * W * 1.04 * 0.84 + 0.02);
  // Collar.
  ring(b, 0.3 * W, 0.05, IRON, TY + 0.42, 5, 12);

  /* back: smokestacks */
  const stacks = B ? [-0.2, 0, 0.2] : [-0.15, 0.15];
  for (const x of stacks) {
    const h = B ? (x === 0 ? 0.75 : 0.6) : 0.5;
    const st = M(new THREE.CylinderGeometry(0.065, 0.08, h, 8), IRON, b);
    st.position.set(x * W, TY + 0.3 + h / 2, -0.32 * W);
    M(new THREE.CylinderGeometry(0.09, 0.09, 0.05, 8), COPPER, b).position.set(x * W, TY + 0.3 + h * 0.65, -0.32 * W);
    M(new THREE.CylinderGeometry(0.1, 0.075, 0.07, 8), DARK, b).position.set(x * W, TY + 0.3 + h, -0.32 * W);
  }
  box(b, IRON, 0.5 * W, 0.42, 0.18, 0, TY + 0.05, -0.36 * W);
  const backGear = gear(b, 0.13, 10, PLATE);
  backGear.position.set(0, TY + 0.05, -0.46 * W);
  backGear.name = 'gearBack';

  /* shoulders */
  const SY = TY + 0.36;
  const SX = 0.56 * W;
  for (const k of [-1, 1]) {
    const dome = M(new THREE.SphereGeometry(B ? 0.27 : 0.21, 10, 7, 0, Math.PI * 2, 0, Math.PI / 1.7), PLATE, b);
    dome.position.set(k * SX, SY, 0);
    dome.rotation.z = -k * 0.35;
    dome.scale.set(1.1, 0.9, 1.1);
    const trim = M(new THREE.TorusGeometry(B ? 0.25 : 0.195, 0.022, 4, 14), COPPER, b);
    trim.position.set(k * (SX + 0.04), SY - 0.05, 0);
    trim.rotation.set(Math.PI / 2, -k * 0.35, 0);
    if (B) {
      for (const [dx, dz] of [[0, 0], [0.08, 0.12], [0.08, -0.12]] as const) {
        const sp = M(new THREE.ConeGeometry(0.045, 0.2, 5), PLATE_HI, b);
        sp.position.set(k * (SX + 0.06 + dx), SY + 0.2, dz);
        sp.rotation.z = -k * 0.5;
      }
    }
  }

  /* head: sunk between the shoulders */
  const HY = TY + 0.46;
  const head = pivot(b, V(0, HY, 0.04));
  head.p.name = 'head';
  const h = head.abs;
  const hs = B ? 1.25 : 1.12;
  box(h, IRON, 0.3 * hs, 0.24 * hs, 0.28 * hs, 0, HY + 0.12 * hs, 0.08);
  const dome = M(new THREE.SphereGeometry(0.16 * hs, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), PLATE, h);
  dome.position.set(0, HY + 0.24 * hs, 0.08);
  dome.scale.set(1, 0.65, 0.95);
  box(h, DARK, 0.24 * hs, 0.06, 0.04, 0, HY + 0.13 * hs, 0.08 + 0.14 * hs);
  eyesGlow(h, CYAN, HY + 0.13 * hs, 0.08 + 0.165 * hs, 0.065 * hs, 0.026);
  // Jaw grille.
  for (let i = 0; i < 4; i++) box(h, PLATE_HI, 0.018, 0.07, 0.02, -0.06 + i * 0.04, HY + 0.04 * hs, 0.08 + 0.145 * hs);
  if (B) {
    for (const k of [-1, 1]) {
      const horn = limb(h, V(k * 0.17, HY + 0.2, 0.06), V(k * 0.32, HY + 0.42, 0.0), 0.05, 0.012, PLATE_HI, 6);
      horn.rotation.z += k * 0.1;
    }
  } else {
    const fin = box(h, COPPER, 0.03, 0.08, 0.24, 0, HY + 0.31, 0.06);
    fin.rotation.x = -0.15;
  }

  /* arms (an elbow pivot carries the forearm, its piston and the fist) */
  const arms: THREE.Group[] = [];
  const elbows: THREE.Group[] = [];
  const hands: THREE.Group[] = [];
  for (const k of [-1, 1]) {
    const ax = k * SX;
    const arm = pivot(b, V(ax, SY - 0.04, 0));
    arm.p.name = k < 0 ? 'armL' : 'armR';
    const a = arm.abs;
    const ex = ax + k * 0.06, ey = SY - 0.48;
    limb(a, V(ax, SY - 0.04, 0), V(ex, ey, 0.04), B ? 0.14 : 0.115, B ? 0.12 : 0.1, IRON, 7);
    const up = M(new THREE.CylinderGeometry(B ? 0.15 : 0.125, B ? 0.13 : 0.11, 0.2, 6), COPPER, a);
    up.position.set(ax + k * 0.035, SY - 0.3, 0.025);
    M(new THREE.SphereGeometry(B ? 0.15 : 0.125, 8, 6), PLATE, a).position.set(ex, ey, 0.04);
    const elbow = pivot(a, V(ex, ey, 0.04));
    elbow.p.name = k < 0 ? 'foreL' : 'foreR';
    elbows.push(elbow.p);
    const fa = elbow.abs;
    const fl = B ? 0.52 : 0.44;
    const fore = M(new THREE.CylinderGeometry(B ? 0.16 : 0.13, B ? 0.21 : 0.17, fl, 7), PLATE, fa);
    fore.position.set(ex + k * 0.01, ey - fl / 2, 0.08);
    M(new THREE.CylinderGeometry(B ? 0.22 : 0.18, B ? 0.22 : 0.18, 0.05, 7), COPPER, fa).position.set(ex + k * 0.01, ey - fl + 0.04, 0.08);
    // Piston along the outside of the forearm.
    limb(fa, V(ex + k * 0.17, ey + 0.05, 0.02), V(ex + k * 0.19, ey - fl + 0.05, 0.06), 0.02, 0.02, PLATE_HI, 5);
    limb(fa, V(ex + k * 0.17, ey + 0.05, 0.02), V(ex + k * 0.18, ey - fl * 0.45, 0.04), 0.035, 0.035, IRON, 6);
    rivet(fa, ex + k * 0.01, ey - 0.08, 0.08 + (B ? 0.15 : 0.12));
    const hand = G(fa, ex + k * 0.01, ey - fl - 0.02, 0.1);
    arms.push(arm.p);
    hands.push(hand);
  }
  const [armL, armR] = arms as [THREE.Group, THREE.Group];
  const [handL, handR] = hands as [THREE.Group, THREE.Group];

  /* right: the hammer (a drum head on a short iron haft) */
  const weapon = G(handR);
  weapon.name = 'weapon';
  const hl = B ? 0.8 : 0.62;
  limb(weapon, V(0, 0.06, 0), V(0, -hl, 0.18), 0.035, 0.035, DARK, 6);
  const head2 = G(weapon, 0, -hl, 0.18);
  head2.rotation.x = Math.atan2(0.18, hl);
  const dr = B ? 0.25 : 0.2, dl = B ? 0.66 : 0.54;
  const drum = M(new THREE.CylinderGeometry(dr, dr, dl, 8), PLATE, head2);
  drum.rotation.z = Math.PI / 2;
  for (const s of [-1, 1]) {
    const face = M(new THREE.CylinderGeometry(dr * 1.08, dr * 1.08, 0.06, 8), IRON, head2);
    face.rotation.z = Math.PI / 2;
    face.position.x = s * dl / 2;
    const band = M(new THREE.CylinderGeometry(dr * 1.04, dr * 1.04, 0.05, 8), COPPER, head2);
    band.rotation.z = Math.PI / 2;
    band.position.x = s * dl * 0.22;
  }
  if (B) {
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const sp = M(new THREE.ConeGeometry(0.04, 0.14, 5), PLATE_HI, head2);
      sp.position.set(0, Math.cos(a) * (dr + 0.05), Math.sin(a) * (dr + 0.05));
      sp.rotation.x = a;
    }
  }

  /* left: a piston fist (bigger and studded on the colossus) */
  const fs = B ? 1.35 : 1;
  const fist = G(handL, 0, -0.06 * fs, 0.02);
  box(fist, PLATE, 0.24 * fs, 0.22 * fs, 0.26 * fs, 0, 0, 0);
  for (let i = 0; i < 4; i++) {
    const kn = M(new THREE.CylinderGeometry(0.035 * fs, 0.035 * fs, 0.06 * fs, 6), IRON, fist);
    kn.rotation.x = Math.PI / 2;
    kn.position.set(-0.08 * fs + i * 0.053 * fs, -0.04 * fs, 0.14 * fs);
    if (B) {
      const st = M(new THREE.ConeGeometry(0.025, 0.07, 5), PLATE_HI, fist);
      st.rotation.x = Math.PI / 2;
      st.position.set(-0.08 * fs + i * 0.053 * fs, -0.04 * fs, 0.2 * fs);
    }
  }
  box(fist, IRON, 0.08 * fs, 0.1 * fs, 0.12 * fs, 0.14 * fs, 0, 0.06 * fs);

  const rig: BotRig = { figure: fig, body: body.p, head: head.p, armL, armR, legL: legs[0]!, legR: legs[1]!, weapon };
  // The rest pose (bones at zero) is the 'idle' frame; the clips (anim/creatures/allies.ts) move from there.
  applyBotPose(rig, 'idle');
  const bones: Record<string, THREE.Object3D> = {
    figure: fig, body: body.p, head: head.p, armL, foreL: elbows[0]!, armR, foreR: elbows[1]!, weapon, legL: legs[0]!, shinL: knees[0]!, legR: legs[1]!, shinR: knees[1]!,
    gearHip: hipGear, gearBack: backGear,
  };
  const skel = { bones: [c.g, ...Object.values(bones)], names: ['root', ...Object.keys(bones)] };
  return { rig, core, skel, height: HY + 0.45 * hs };
};

const golemModel = (o: BaseOpts | undefined, big: boolean): BotModel => {
  const c = begin(o, big ? 0.95 : 0.75, big ? 41 : 40);
  const { rig, core, skel, height } = clockGolem(c, { big });
  const coreMat = core.material as THREE.SpriteMaterial;
  // Only the core's glow boils here; the gears (bones) turn and the body moves in the clips.
  const t = (n: number): void => {
    const k = 0.5 + 0.5 * Math.sin(n * 0.9);
    coreMat.opacity = 0.55 + 0.3 * k;
    core.scale.setScalar((big ? 0.6 : 0.45) * (0.9 + 0.2 * k));
  };
  t(0);
  const m = done(c, height, t, undefined, skel);
  return { ...m, pose: (p) => applyBotPose(rig, p) };
};

/** A clockwork automaton: dark iron, steel plate, a cyan core; a drum hammer in the right hand, a piston fist in the left. */
export const automaton = (o?: BaseOpts): BotModel => golemModel(o, false);

/** The colossus: the automaton's heavier sibling, built broader with spikes, horns, three stacks and a spiked maul. */
export const colossus = (o?: BaseOpts): BotModel => golemModel(o, true);

/** A spider-bot: a round ticking body with a red eye, a cyan charge in its tail, and six jointed legs (tripod gait). */
export const spiderBot = (o?: BaseOpts): Model => {
  const c = begin(o, 0.45, 42);
  const body = G(c.g, 0, 0.3, 0);
  body.name = 'body';
  const shell = M(new THREE.SphereGeometry(0.2, 12, 9), IRON, body);
  shell.scale.set(1, 0.72, 1.12);
  const top = M(new THREE.SphereGeometry(0.205, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2.4), PLATE, body);
  top.scale.set(1, 0.74, 1.13);
  top.position.y = 0.01;
  for (const k of [-1, 0, 1]) {
    const seam = box(body, DARK, 0.016, 0.02, 0.36, k * 0.07, 0.135 - Math.abs(k) * 0.015, 0);
    seam.rotation.z = k * -0.3;
  }
  ring(body, 0.2, 0.018, COPPER, 0, 4, 18).scale.set(1, 1.12, 1);
  // The eye: one red lens in a steel socket.
  const socket = M(new THREE.TorusGeometry(0.06, 0.018, 5, 12), PLATE_HI, body);
  socket.position.set(0, 0.02, 0.215);
  M(new THREE.SphereGeometry(0.052, 8, 6), RED_EYE, body).position.set(0, 0.02, 0.205);
  const eye = glowSprite(body, '#ff4f3a', 0.36, V(0, 0.02, 0.25), 0.9, true);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.018, 5, 4), CORE, body).position.set(k * 0.1, 0.06, 0.18);
    // Mandible clamps.
    const md = limb(body, V(k * 0.06, -0.06, 0.18), V(k * 0.035, -0.12, 0.27), 0.018, 0.008, PLATE_HI, 5);
    md.rotation.y += k * 0.2;
  }
  // The charge: a cyan canister behind, in an iron cage.
  const can = G(body, 0, 0.02, -0.22);
  can.name = 'charge';
  can.rotation.x = Math.PI / 2 - 0.3;
  M(new THREE.CylinderGeometry(0.075, 0.075, 0.16, 10), CORE_SOFT, can);
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    limb(can, V(Math.cos(a) * 0.08, -0.09, Math.sin(a) * 0.08), V(Math.cos(a) * 0.08, 0.09, Math.sin(a) * 0.08), 0.012, 0.012, IRON, 4);
  }
  ring(can, 0.085, 0.015, COPPER, 0.08, 4, 12);
  ring(can, 0.085, 0.015, COPPER, -0.08, 4, 12);
  glowSprite(can, CYAN, 0.3, V(0, 0, 0), 0.6);
  // A wind-up key on top.
  const key = G(body, 0, 0.17, -0.04);
  limb(key, V(0, -0.02, 0), V(0, 0.08, 0), 0.014, 0.014, PLATE_HI, 5);
  for (const k of [-1, 1]) {
    const bow = M(new THREE.TorusGeometry(0.035, 0.012, 4, 10), PLATE_HI, key);
    bow.position.set(k * 0.04, 0.1, 0);
  }

  // Six legs, three a side, splayed: [side, angle from the side axis].
  // Bones: each leg turns at the hip (`hipL0`…, yaw about Y) and lifts at it (`liftL0`…, about the leg's own Z);
  // 0 is the back leg of a side, 2 the front one. The clips (anim/creatures/allies.ts) move them.
  const bones: Record<string, THREE.Object3D> = { body, charge: can };
  for (const k of [-1, 1]) {
    for (let j = 0; j < 3; j++) {
      const phi = (k > 0 ? 0 : Math.PI) + k * (j - 1) * 0.75;
      const yaw = G(body, Math.cos(phi) * 0.15, -0.02, Math.sin(phi) * 0.17);
      yaw.rotation.y = -phi;
      const lift = G(yaw);
      const side = k < 0 ? 'L' : 'R';
      yaw.name = `hip${side}${j}`;
      lift.name = `lift${side}${j}`;
      bones[yaw.name] = yaw;
      bones[lift.name] = lift;
      limb(lift, V(0, 0, 0), V(0.18, 0.12, 0), 0.026, 0.02, IRON, 5);
      M(new THREE.SphereGeometry(0.03, 6, 4), COPPER, lift).position.set(0.18, 0.12, 0);
      limb(lift, V(0.18, 0.12, 0), V(0.32, -0.28, 0), 0.02, 0.01, PLATE, 5);
      const tip = M(new THREE.ConeGeometry(0.014, 0.05, 4), PLATE_HI, lift);
      tip.position.set(0.325, -0.3, 0);
      tip.rotation.z = Math.PI;
    }
  }
  const eyeMat = eye.material as THREE.SpriteMaterial;
  // Only the key and the eye boil here; the scuttle (tripod gait) is a clip.
  const t = (n: number): void => {
    key.rotation.y = n * 0.9;
    eyeMat.opacity = n % 4 < 2 ? 1 : 0.6;
  };
  t(0);
  return done(c, 0.5, t, undefined, { bones: [c.g, ...Object.values(bones)], names: ['root', ...Object.keys(bones)] });
};

/** The gear grenade as it lies on the ground: claws in the earth, a toothed rim that spins around a cyan core. */
export const gearGrenade = (o?: BaseOpts): Model => {
  const c = begin(o, 0.4, 43);
  const g = c.g;
  M(new THREE.CylinderGeometry(0.2, 0.24, 0.08, 10), DARK, g).position.y = 0.04;
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.3;
    const claw = limb(g, V(Math.cos(a) * 0.18, 0.06, Math.sin(a) * 0.18), V(Math.cos(a) * 0.34, -0.02, Math.sin(a) * 0.34), 0.03, 0.01, COPPER, 5);
    claw.castShadow = false;
  }
  const rim = G(g, 0, 0.12, 0);
  const gr = gear(rim, 0.22, 12, PLATE, 0.06);
  gr.rotation.x = Math.PI / 2;
  const inner = G(g, 0, 0.17, 0);
  const gi = gear(inner, 0.12, 8, COPPER, 0.04);
  gi.rotation.x = Math.PI / 2;
  const dome = M(new THREE.SphereGeometry(0.09, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), CORE, g);
  dome.position.y = 0.19;
  M(new THREE.TorusGeometry(0.095, 0.015, 4, 12), IRON, g).rotation.x = Math.PI / 2;
  (g.children[g.children.length - 1] as THREE.Object3D).position.y = 0.19;
  glowSprite(g, CYAN, 0.55, V(0, 0.24, 0), 0.85);
  const t = (n: number): void => {
    rim.rotation.y = n * 0.45;
    inner.rotation.y = -n * 0.8;
  };
  t(0);
  return done(c, 0.3, t);
};
